import { useState, useEffect, useRef } from 'react'
import { ItemRenderer } from './ItemRenderer.jsx'
import { isRequired, isAnswered, isRenderable } from './kinds.js'
import { SaveFailure } from './SaveFailure.jsx'
import { paginate, sectionLayout } from './paginate.js'
import { PrimaryButton, SecondaryButton } from '../ui/Screen.jsx'

/**
 * One item at a time — everything that does not grid.
 *
 * pageSize stays 1 for day 0. A beep may pass 2 or 3 for a short stack; that is
 * the only difference between the two uses, so there is one component.
 *
 * WHICH ITEMS MUST BE ANSWERED IS NOT DECIDED HERE.
 *
 * Day 0 passes `requiredIds`, derived from the server's optional_item_ids:
 * required is the default and optional is the only declared state. A participant
 * may not skip a non-optional question (operator ruling, architecture.md).
 *
 * A beep passes no requiredIds and falls back to item.is_safety, which is the
 * separate EMA rule and is unchanged.
 */
export function ItemStack({
  items,
  formats,
  answers,
  onAnswer,
  onComplete,
  // Kept only for callers that genuinely want a fixed page (the dev preview).
  // Day 0 and beeps paginate BY COST — see paginate.js. Item count is the wrong
  // unit: a row of marks is cheap, a stack of option cards costs a line each.
  pageSize = null,
  maxItems = null,
  disabled = false,
  instruction,
  // The timeframe stays visible on every screen; the instruction does not.
  timeframe,
  // TRUE when the caller already shows the timeframe persistently (Day0 puts
  // it in the ScreenTitle subtitle on every page of the part). This stack must
  // then never print it again: "Past year" under "Part 9 of 15" and "Past
  // year" again two lines down was the exact repetition the operator flagged.
  // The line here exists for callers whose title does NOT carry it - suppress,
  // don't delete, or those callers lose the timeframe entirely.
  timeframeInTitle = false,
  // Day-0: the set of item ids that must be answered. Absent for beeps.
  requiredIds = null,
  // Day-0: the server's own list of outstanding required ids for this section.
  // It, not this component, decides whether the section may be left.
  sectionOutstanding = null,
  // Position within a battery. Off by default: the app's progress indicator is
  // beeps out of 30 and the current day, and nothing else.
  showPosition = false,
  continueLabel = 'Continue',
  finishLabel = 'Done',
  // DS-2 §3.9. item_id -> the error from its last failed save. The message
  // belongs against the item it is about, so the map is passed down rather than
  // one error being raised to the screen.
  failures = null,
  onRetry = null,
  // Day 0, Back across parts: when she steps INTO a section from the one after
  // it, she lands on its last page, not on "the first thing still needing an
  // answer" (nothing does, in a section she completed).
  startAtEnd = false,
  /*
   * AD-3 §3 — THE ACTION LIVES IN THE BAND, NOT IN THE BODY.
   *
   * The stack still owns pagination, because where she is inside a section is
   * this component's business and nothing else's. What it no longer owns is
   * WHERE ITS BUTTONS ARE DRAWN: the primary belongs in the ActionBar and the
   * back control in the NavBar, so both are reported upward and the screen
   * places them.
   *
   * That is what kills F6. The most-tapped control in the study sat 29% down
   * the screen with 572px of dead space beneath it, because it was rendered
   * wherever the content happened to end.
   */
  onAction = null,
}) {
  const [page, setPage] = useState(0)
  // §3.8: the incomplete state is not entered until the participant asks to
  // leave. Nothing is flagged on arrival.
  const [asked, setAsked] = useState(false)

  // Layout is uniform across the SECTION, so a participant never meets two
  // different controls inside one instrument.
  const layout = sectionLayout(items, formats)
  const screens = pageSize
    ? items.reduce((acc, item, i) => {
        const at = Math.floor(i / pageSize)
        ;(acc[at] ??= []).push(item)
        return acc
      }, [])
    : paginate(items, formats, { layout, maxItems })
  const pageCount = Math.max(1, screens.length)

  // An item that cannot be rendered cannot be answered, so it must never block.
  const mustAnswer = (item) =>
    requiredIds ? requiredIds.has(item.item_id) : isRequired(item)

  const needsAnswer = (item) =>
    mustAnswer(item) &&
    isRenderable(formats[item.response_format]) &&
    !isAnswered(answers?.[item.item_id])

  /**
   * WHERE THE PARTICIPANT IS IS CLIENT STATE. It is not re-derived from server
   * data, and this is the seam that broke.
   *
   * This reset used to depend on `items` — the array itself. Day 0 re-reads
   * day0/state after every save, which produces a NEW array with the same
   * contents, so selecting any answer reset the view to item 1 of the section.
   * To answer item n you had to press Continue n-1 times to walk back: 55 taps
   * for a 10-item instrument, 210 for a 20-item one, across 177-223 items. The
   * answers all saved, every server check stayed green, and the participant was
   * the only one who ever experienced it.
   *
   * Keyed on the item IDS instead, so it fires when the participant genuinely
   * moves to a different section or beep and never on a refetch of the same
   * one. The server is authoritative about WHAT must be answered; it is not
   * authoritative about which screen someone is looking at.
   */
  const itemsKey = items.map((i) => i.item_id).join('|')

  useEffect(() => {
    // And land on the first thing still needing an answer, not on item 1.
    // Returning to a 20-item instrument should not mean walking back past
    // everything already done.
    const index = items.findIndex(needsAnswer)
    const at = index < 0 ? 0 : screens.findIndex((s) => s.includes(items[index]))
    setPage(startAtEnd && index < 0 ? Math.max(0, screens.length - 1) : at < 0 ? 0 : at)
    setAsked(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, pageSize, maxItems])


  const shown = screens[Math.min(page, pageCount - 1)] ?? []
  const isLast = page >= pageCount - 1
  const start = items.indexOf(shown[0] ?? items[0])
  const blocking = shown.filter(needsAnswer)
  const blocked = blocking.length > 0

  // Leaving the SECTION is the server's call, taken verbatim from
  // required_outstanding. Paging within it is this component's, above.
  const outstandingIds = sectionOutstanding ?? []
  const sectionBlocked = isLast && outstandingIds.length > 0

  // Assigned below, after `next` exists.
  function next() {
    /*
     * TWO INSTRUCTIONS MEET HERE AND BOTH ARE HONOURED.
     *
     * NS-3 (operator): a participant may not advance past an unanswered
     * required item. DS-2 §3.8 (design): the incomplete state is not entered
     * until they ASK to leave — which is impossible if the control cannot be
     * pressed at all.
     *
     * So the action is aria-disabled rather than disabled: it stays focusable
     * and pressable, pressing it NEVER advances, and pressing it is what names
     * what is missing. Nothing can be skipped, and nothing scolds on arrival.
     * A hard `disabled` would also have made this unreachable by keyboard.
     */
    if (blocked || sectionBlocked) {
      /*
       * AD-5 defect 4 — MOVE FOCUS TO THE FIRST OUTSTANDING ITEM.
       *
       * Pressing a live primary on an incomplete screen is how she learns what
       * is missing, and focus was landing on BODY: a screen-reader user was
       * told nothing and left at the top of the document. §4.3 requires the
       * note AND the focus move, because the note alone is only visible help.
       */
      setAsked(true)
      /*
       * FOCUS BY CONTROL NAME, NOT BY [data-item-id].
       *
       * The wrapper attribute only exists on GRID rows, so on a stacked screen
       * this query matched nothing and focus stayed on BODY — measured after
       * pressing on /day0 and /consent. Nothing visible moved and nothing
       * focusable moved, which compounded the note being below the fold.
       *
       * Every option control carries `name={item_id}`, which is true on both
       * layouts, so that is what is targeted. Radios are `sr-only`, so focus
       * goes to the input and the browser scrolls its LABEL into view — the
       * visible thing — rather than a 1px box.
       */
      const firstMissing = items.find(needsAnswer)
      if (firstMissing) {
        requestAnimationFrame(() => {
          const control = document.querySelector(
            `[name="${firstMissing.item_id}"], #${CSS.escape(firstMissing.item_id)}`,
          )
          if (!control) return
          const visible = control.closest('label') ?? control
          visible.scrollIntoView({ block: 'center' })
          control.focus?.()
        })
      }
      return
    }
    setAsked(false)
    if (isLast) return onComplete?.()
    if (page === 0 && !(window.history.state && window.history.state.editricsStack)) {
      window.history.pushState({ editricsStack: true }, '')
    }
    setPage((p) => p + 1)
    // A new page starts at its top. Without this the next page arrived
    // pre-scrolled to wherever the last one ended (the operator saw it on
    // desktop; the container scroll position simply persists).
    scrollTop()
  }

  /*
   * Reported through a ref so the effect depends on VALUES and never on the
   * parent's callback identity — a parent that re-creates its handler each
   * render would otherwise make this an infinite loop.
   */
  const actionRef = useRef(onAction)
  actionRef.current = onAction
  /*
   * Hoisted out of the effect deliberately. Inside it, the reporter would
   * CONTAIN a setPage call — handing one out, not making one — and the harness
   * guard that counts "effects that move the page" cannot tell those apart from
   * source. Rather than teach the guard about nesting, the callback lives here
   * so the effect body genuinely has no setPage in it. The invariant the guard
   * protects is real: exactly one thing decides where she is.
   */
  // The scroll container is <main> on desktop and the window on mobile;
  // asking both is cheaper than knowing which.
  const scrollTop = () => {
    requestAnimationFrame(() => {
      document.querySelector('main')?.scrollTo?.({ top: 0 })
      window.scrollTo({ top: 0 })
    })
  }
  const goBack = () => setPage((p) => Math.max(0, p - 1))
  /*
   * THE PHONE'S OWN BACK STEPS ONE QUESTION, NOT OUT OF THE SCREEN.
   *
   * Paging never touched browser history, so Android's back gesture (the
   * natural thing to press) left the check-in entirely and read as "there is
   * no way back". One marker entry is pushed the first time she moves off
   * page 0; the gesture pops it, this steps back a page and re-pushes the
   * marker, so inside the stack the gesture always means "previous question".
   * On page 0 nothing is re-pushed and the gesture leaves, exactly as before.
   * The URL never changes, so the router sees nothing.
   */
  const pageRef = useRef(0)
  pageRef.current = page
  useEffect(() => {
    const onPop = (event) => {
      if (pageRef.current <= 0) return
      goBack()
      window.history.pushState({ editricsStack: true }, '')
      event.stopImmediatePropagation?.()
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  /*
   * Hoisted for the same reason as goBack: inside the reporting effect this
   * would CONTAIN a setPage call — handed out, not made — and the guard that
   * counts "effects that move the page" cannot tell those apart from source.
   * The invariant it protects is real, so the code satisfies it honestly
   * rather than being excused from it.
   */
  /*
   * THE BAND HOLDS A CALLBACK ACROSS RENDERS, so it must never hold a STALE
   * one.
   *
   * `press: next` reported the function as it existed when the effect last ran.
   * `next` closes over `page`, `blocked` and `isLast`, so between an answer
   * landing and the effect re-running, the ActionBar could be holding a
   * closure that believes the participant is somewhere she no longer is —
   * pressing Continue would then re-evaluate the wrong page.
   *
   * The reported function is stable and reads the LATEST `next` out of a ref
   * instead, so the band's button is always calling current logic no matter
   * when it was handed over. The same reason the action itself is reported by
   * value: the stack owns where she is, and nothing else may hold a copy of it.
   */
  const nextRef = useRef(null)
  const pressStable = () => nextRef.current?.()
  nextRef.current = next
  const actionLabel = isLast ? finishLabel : continueLabel
  const actionBlocked = blocked || sectionBlocked
  useEffect(() => {
    actionRef.current?.({
      label: actionLabel,
      press: pressStable,
      incomplete: actionBlocked,
      canGoBack: page > 0,
      goBack,
      /*
       * The note travels WITH the action, so the screen can put it in the band
       * directly above the primary. It is built here because only this
       * component knows which page each outstanding item is on.
       */
      /*
       * NO NOTICE. The "N still need an answer / Take me to it" line is gone
       * by operator ruling: the flagged rows already carry the red mark, the
       * press already scrolls to the first missing item, and the walker's
       * jumpTo was resetting page state under the participant. A control that
       * loses her answers to show her a count is worse than no control.
       */
      notice: null,
    })
     
  }, [actionLabel, actionBlocked, page, disabled, asked, outstandingIds.length])

  return (
    <div>
      {/*
        §2.5 — THE INSTRUCTION IS READ ONCE, NOT ONCE PER ITEM.
        EPDS repeated "In the past 7 days:" above all ten of its items, and
        PCL-5's 46-word instruction rendered twenty times at the same size as
        the question, so it outranked the item on every one of those screens.
        The words are config's and none of them change; only the repetition
        does. The timeframe is the part that must stay visible, so it persists
        as a meta line while the rest is shown once, on the first screen.
      */}
      {/*
        THE INSTRUCTION PERSISTS ON EVERY PAGE OF THE SECTION - operator
        ruling, reversing the show-once rule. Page 3 of PCL-5 without its
        instruction is a list of statements with no verb: "how much were you
        bothered by" lives only in the instruction, so later pages were asking
        her to rate sentences against a criterion that had scrolled away two
        Continues ago. The repetition the old rule killed was PER-ITEM; once
        per PAGE is the reading unit, not noise.
      */}
      {instruction ? (
        <p className="t-instruction mb-[var(--s-4)]">{instruction}</p>
      ) : timeframe && !timeframeInTitle ? (
        <p className="t-meta mb-[var(--s-4)]">{timeframe}</p>
      ) : null}

      {showPosition ? (
        <p className="t-meta mb-[var(--s-3)]">
          {Math.min(start + shown.length, items.length)} of {items.length}
        </p>
      ) : null}

      <div className="flex flex-col gap-8">
        {shown.map((item) => {
          /*
           * THE RED MARK, FOR EVERYTHING THAT IS NOT A GRID. Grid rows already
           * flag outstanding items (critical left rule, sunken fill) - stacked
           * items had NO visible needed-state at all: pressing Continue
           * scrolled to the first missing item and then showed nothing on it,
           * so what the scroll meant was a guess. Same two channels as the
           * grid (rule + text, not colour alone), gated on `asked` exactly
           * like the grid's flags: nothing is red on arrival, only after she
           * asks to leave. It clears the moment the item is answered, because
           * needsAnswer goes false - no bookkeeping.
           */
          const flagged = asked && needsAnswer(item)
          return (
          <div
            key={item.item_id}
            className={flagged ? 'border-l-2 border-l-[var(--critical)] pl-[var(--s-3)]' : ''}
          >
            {flagged ? (
              <p className="t-meta mb-[var(--s-1)] font-semibold text-[var(--critical)]">
                This one is needed.
              </p>
            ) : null}
            <ItemRenderer
              item={item}
              format={formats[item.response_format]}
              answer={answers?.[item.item_id] ?? null}
              onChange={(nextAnswer) => {
                onAnswer(item, nextAnswer)
                /*
                 * AUTO-ADVANCE, IN THE HANDLER, NEVER IN AN EFFECT. Single
                 * -page sections advance on their own (server-led) the moment
                 * the last answer lands; multi-page sections stranded her on a
                 * finished page behind a Continue press - the inconsistency
                 * the operator flagged. Completing the page IS her action, so
                 * her action moves her: the guard's invariant ("exactly one
                 * thing decides where she is") holds because this is the same
                 * hand that picked the answer.
                 *
                 * The answer just chosen is not in `answers` yet (the parent's
                 * refetch is in flight), so the page is checked with the new
                 * value substituted. 350ms so the radio visibly fills before
                 * the screen moves - instant jumps read as the tap misfiring.
                 * The LAST page never auto-advances from here: its completion
                 * belongs to the server, which moves the whole section.
                 */
                if (!isLast && isAnswered(nextAnswer)) {
                  const pageDone = shown.every(
                    (i) => i.item_id === item.item_id || !needsAnswer(i),
                  )
                  if (pageDone) {
                    /*
                     * GUARDED AGAINST THE DOUBLE ADVANCE. She can press
                     * Continue inside the 350ms beat (so can a harness), and
                     * the queued timer would then advance a SECOND time,
                     * skipping a page whose items were never shown. The timer
                     * only fires if she is still on the page it was scheduled
                     * from; any other movement wins and the timer is a no-op.
                     */
                    const from = page
                    setTimeout(() => {
                      setPage((prev) => (prev === from ? prev + 1 : prev))
                      scrollTop()
                    }, 350)
                  }
                }
              }}
              disabled={disabled}
            />
            {/* Directly beneath the item it is about — §3.9. Not raised to the
                screen, where it would be a claim about the whole check-in. */}
            <SaveFailure
              error={failures?.[item.item_id] ?? null}
              onRetry={onRetry ? () => onRetry(item) : null}
            />
          </div>
          )
        })}
      </div>

      {/*
        NOTHING ON ARRIVAL.
        "Please answer this one before continuing" used to render the moment an
        unanswered item appeared — telling someone off for not yet doing the
        thing they had just been shown, on every one of 200+ items, on a form
        about their mental health.
        It cannot fire on an attempt either, because Continue is disabled while
        the item is unanswered, so there is no attempt to make. Rather than keep
        a message that can only ever be premature, it is gone: the section-level
        note below names what is outstanding where a screen holds several items,
        and on a one-item screen the disabled Continue sits directly under the
        single unanswered question.
        Whether a mid-stack explanation should exist at all is design's
        (DS-1 finding 8, inverted help) — not something to invent here.
      */}

      {/* Named rather than left to guesswork, and only for the section gate —
          the per-page case above is already on screen in front of them. */}
      {/* Only when nobody upstream is placing them (the dev previews). */}
      {onAction ? null : (
        <div className="mt-8 flex flex-col gap-3">
          <PrimaryButton onClick={next} disabled={disabled} incomplete={actionBlocked}>
            {actionLabel}
          </PrimaryButton>
          {page > 0 ? (
            <SecondaryButton onClick={() => setPage((p) => p - 1)} disabled={disabled}>
              Back
            </SecondaryButton>
          ) : null}
        </div>
      )}
    </div>
  )
}
